import { createContext, useState, useEffect, useCallback } from "react";
import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import axios from "axios";

const firebaseConfig = JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG);
initializeApp(firebaseConfig);

export const UserContext = createContext({
  currentUser: null,
  setCurrentUser: () => null,
});

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authUser, loading] = useAuthState(getAuth());
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const [hasFetchedUser, setHasFetchedUser] = useState(false);

  // Função para buscar dados do usuário do servidor
  const fetchUserData = useCallback(
    async (uid) => {
      if (isLoadingUserData) return; // Evitar múltiplas chamadas

      setHasFetchedUser(false);
      setIsLoadingUserData(true);
      try {
        const idToken = await authUser.getIdToken();
        const response = await axios.get(`/api/auth/user/${uid}`, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });
        setCurrentUser(response.data);
      } catch (error) {
        console.error("Error fetching user data:", error);
        // Se falhar ao buscar dados do usuário, limpar o estado
        setCurrentUser(null);
      } finally {
        setIsLoadingUserData(false);
        setHasFetchedUser(true);
      }
    },
    [authUser, isLoadingUserData]
  );

  // Efeito para sincronizar currentUser com authUser
  useEffect(() => {
    if (!loading) {
      if (authUser && !currentUser && !isLoadingUserData && !hasFetchedUser) {
        // Se authUser existe mas currentUser não, buscar dados do usuário
        fetchUserData(authUser.uid);
      } else if (!authUser) {
        // Se não há authUser, limpar currentUser
        setCurrentUser(null);
        setHasFetchedUser(false);
      }
    }
  }, [
    authUser,
    loading,
    currentUser,
    isLoadingUserData,
    hasFetchedUser,
    fetchUserData,
  ]);

  const value = {
    currentUser,
    setCurrentUser,
    authUser,
    isLoadingUserData,
    hasFetchedUser,
    setHasFetchedUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
